import { Button } from "../../design-system";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HiOutlineBookmark, HiOutlineHome } from "react-icons/hi2";
import { RiPlayList2Line } from "react-icons/ri";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import { db, storage } from "../../firebase/firebase";
import AlbumPanel from "./components/AlbumPanel";
import AlbumSelectionPage from "./components/AlbumSelectionPage";
import TrackListPanel from "./components/TrackListPanel";
import PlayerFooter from "./components/PlayerFooter";
import YoutubePlayer, { YoutubePlayerHandle } from "./components/YoutubePlayer";
import { AlbumData, TrackData } from "./Playlist.types";

const sidebarItems = [
  { icon: <HiOutlineHome size={22} />, label: "Início" },
  //   { icon: <MdOutlineExplore size={22} />, label: "Explorar" },
  { icon: <RiPlayList2Line size={22} />, label: "Biblioteca" },
];

const bottomTabs = [
  { icon: <HiOutlineHome size={25} />, label: "Início" },
  // { icon: <MdOutlineForward30 size={25} />, label: "Descobertas" },
  // { icon: <IoCompassOutline size={25} />, label: "Explorar" },
  { icon: <HiOutlineBookmark size={25} />, label: "Biblioteca" },
];

const emptyAction = () => { };

const formatDuration = (durationSec?: number) => {
  const safeDuration = durationSec || 0;
  const minutes = Math.floor(safeDuration / 60);
  const seconds = safeDuration % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const formatPlayCount = (playCount?: number) => {
  if (!playCount) {
    return "";
  }

  return ""; //`Tocou ${playCount.toLocaleString("pt-BR")} vezes`;
};

function Playlist() {
  const navigate = useNavigate();
  const { albumId } = useParams<{ albumId: string }>();
  const targetAlbumId = albumId || "";
  const activeMenuLabel = "Biblioteca";

  const [albumData, setAlbumData] = useState<AlbumData | null>(null);
  const [tracksData, setTracksData] = useState<TrackData[]>([]);
  const [activeTrackId, setActiveTrackId] = useState<string>("");
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [currentTimeSec, setCurrentTimeSec] = useState<number>(0);
  const [audioDurationSec, setAudioDurationSec] = useState<number>(0);
  const [isDesktopSeekHoverVisible, setIsDesktopSeekHoverVisible] =
    useState<boolean>(false);
  const [desktopSeekHoverPercent, setDesktopSeekHoverPercent] =
    useState<number>(0);
  const [desktopSeekHoverTimeSec, setDesktopSeekHoverTimeSec] =
    useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [availableAlbums, setAvailableAlbums] = useState<AlbumData[]>([]);
  const [isAlbumsLoading, setIsAlbumsLoading] = useState<boolean>(false);
  const [albumsErrorMessage, setAlbumsErrorMessage] = useState<string>("");
  const [isDesktopViewport, setIsDesktopViewport] = useState<boolean>(
    () => window.innerWidth >= 768,
  );
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const desktopYoutubeRef = useRef<YoutubePlayerHandle | null>(null);
  const mobileYoutubeRef = useRef<YoutubePlayerHandle | null>(null);
  const desktopSeekBarRef = useRef<HTMLDivElement | null>(null);
  const mobileSeekBarRef = useRef<HTMLDivElement | null>(null);
  const previousIsDesktopViewportRef = useRef<boolean>(isDesktopViewport);
  const pendingYoutubeResumeRef = useRef<{
    videoId: string;
    timeSec: number;
    shouldPlay: boolean;
  } | null>(null);
  const autoAdvancedTrackIdRef = useRef<string>("");
  const autoAdvanceArmedRef = useRef<boolean>(false);
  const handleNextTrackRef = useRef<(forcePlay?: boolean) => Promise<void>>(
    async () => { },
  );

  const pauseYoutubePlayers = () => {
    desktopYoutubeRef.current?.pause();
    mobileYoutubeRef.current?.pause();
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const syncViewport = () => {
      setIsDesktopViewport(mediaQuery.matches);
    };

    syncViewport();
    mediaQuery.addEventListener("change", syncViewport);

    return () => {
      mediaQuery.removeEventListener("change", syncViewport);
    };
  }, []);

  useEffect(() => {
    if (!albumId) {
      return;
    }

    const loadAlbum = async () => {
      setIsLoading(true);
      setErrorMessage("");

      try {
        const albumRef = doc(db, "albums", targetAlbumId);
        const albumSnapshot = await getDoc(albumRef);

        if (!albumSnapshot.exists()) {
          setAlbumData(null);
          setTracksData([]);
          setErrorMessage("Álbum não encontrado.");
          setIsLoading(false);
          return;
        }

        const albumRaw = albumSnapshot.data() as Omit<AlbumData, "id">;
        let resolvedCoverUrl = albumRaw.coverUrl;

        if (albumRaw.coverPath) {
          try {
            resolvedCoverUrl = await getDownloadURL(
              ref(storage, albumRaw.coverPath),
            );
          } catch (error) {
            console.error("Failed to load cover from Storage path", error);
          }
        }

        const tracksQuery = query(
          collection(db, "albums", targetAlbumId, "tracks"),
          orderBy("order", "asc"),
        );

        const tracksSnapshot = await getDocs(tracksQuery);

        const loadedTracks: TrackData[] = await Promise.all(
          tracksSnapshot.docs.map(async (trackDoc) => {
            const trackRaw = trackDoc.data() as Omit<TrackData, "id">;
            let resolvedAudioUrl = trackRaw.audioUrl;

            if (trackRaw.audioPath) {
              try {
                resolvedAudioUrl = await getDownloadURL(
                  ref(storage, trackRaw.audioPath),
                );
              } catch (error) {
                console.error("Failed to load audio from Storage path", error);
              }
            }

            return {
              id: trackDoc.id,
              ...trackRaw,
              audioUrl: resolvedAudioUrl,
            };
          }),
        );

        setAlbumData({
          id: albumSnapshot.id,
          ...albumRaw,
          coverUrl: resolvedCoverUrl,
        });
        setTracksData(loadedTracks);
        setActiveTrackId(loadedTracks[0]?.id || "");
        setIsPlaying(false);
        setCurrentTimeSec(0);
        setAudioDurationSec(0);
      } catch (error) {
        console.error(error);
        setErrorMessage("Erro ao carregar o álbum.");
      } finally {
        setIsLoading(false);
      }
    };

    loadAlbum();
  }, [albumId, targetAlbumId]);

  useEffect(() => {
    if (albumId) {
      return;
    }

    const loadAlbums = async () => {
      setIsAlbumsLoading(true);
      setAlbumsErrorMessage("");

      try {
        let albumsSnapshot = null;

        try {
          albumsSnapshot = await getDocs(collection(db, "albums"));
        } catch (error) {
          console.error("Failed to load albums collection", error);
        }

        if (!albumsSnapshot || albumsSnapshot.empty) {
          try {
            albumsSnapshot = await getDocs(collection(db, "albuns"));
          } catch (error) {
            console.error("Failed to load albuns collection", error);
          }
        }

        if (!albumsSnapshot) {
          setAlbumsErrorMessage("Erro ao carregar álbuns.");
          setAvailableAlbums([]);
          return;
        }

        const loadedAlbums = await Promise.all(
          albumsSnapshot.docs.map(async (albumDoc) => {
            const albumRaw = albumDoc.data() as Omit<AlbumData, "id">;
            let resolvedCoverUrl = albumRaw.coverUrl;

            if (albumRaw.coverPath) {
              try {
                resolvedCoverUrl = await getDownloadURL(
                  ref(storage, albumRaw.coverPath),
                );
              } catch (error) {
                console.error("Failed to load cover from Storage path", error);
              }
            }

            return {
              id: albumDoc.id,
              ...albumRaw,
              coverUrl: resolvedCoverUrl,
            };
          }),
        );

        loadedAlbums.sort((firstAlbum, secondAlbum) => {
          const firstYear = firstAlbum.year || 0;
          const secondYear = secondAlbum.year || 0;

          if (firstYear !== secondYear) {
            return secondYear - firstYear;
          }

          return firstAlbum.title.localeCompare(secondAlbum.title, "pt-BR");
        });

        setAvailableAlbums(loadedAlbums);
      } catch (error) {
        console.error(error);
        setAlbumsErrorMessage("Erro ao carregar álbuns.");
      } finally {
        setIsAlbumsLoading(false);
      }
    };

    loadAlbums();
  }, [albumId]);

  const activeTrack = useMemo(() => {
    if (!tracksData.length) {
      return null;
    }

    return (
      tracksData.find((track) => track.id === activeTrackId) || tracksData[0]
    );
  }, [activeTrackId, tracksData]);

  useEffect(() => {
    const player = audioRef.current;

    if (!player) {
      return;
    }

    if (!activeTrack?.audioUrl) {
      player.pause();
      player.removeAttribute("src");
      player.load();
      setIsPlaying(false);
      setCurrentTimeSec(0);
      setAudioDurationSec(0);
      return;
    }

    if (player.src !== activeTrack.audioUrl) {
      player.src = activeTrack.audioUrl;
      player.load();
      setIsPlaying(false);
      setCurrentTimeSec(0);
      setAudioDurationSec(0);
    }
  }, [activeTrack]);

  const playTrack = async (track: TrackData) => {
    const player = audioRef.current;

    if (!player || !track.audioUrl) {
      return;
    }

    pauseYoutubePlayers();

    setActiveTrackId(track.id);

    if (player.src !== track.audioUrl) {
      player.src = track.audioUrl;
      player.load();
    }

    try {
      await player.play();
    } catch (error) {
      console.error("Failed to start audio playback", error);
      setIsPlaying(false);
    }
  };

  const handlePlayStop = async () => {
    const player = audioRef.current;
    const isYoutubeTrack = !!(activeTrack?.youtubeId && !activeTrack?.audioPath);
    const activeYoutubePlayerRef = isDesktopViewport
      ? desktopYoutubeRef.current
      : mobileYoutubeRef.current;

    if (!activeTrack) {
      return;
    }

    if (isYoutubeTrack && activeYoutubePlayerRef) {
      if (isPlaying) {
        activeYoutubePlayerRef.pause();
        setIsPlaying(false);
      } else {
        player?.pause();
        activeYoutubePlayerRef.play();
        setIsPlaying(true);
      }

      return;
    }

    if (isPlaying && player) {
      player.pause();
      return;
    }

    await playTrack(activeTrack);
  };

  const activateTrack = async (track: TrackData, shouldPlay: boolean) => {
    const player = audioRef.current;
    const isYoutubeTrack = !!(track.youtubeId && !track.audioUrl);

    setActiveTrackId(track.id);
    setCurrentTimeSec(0);
    setAudioDurationSec(0);

    if (isYoutubeTrack) {
      if (player) {
        player.pause();
      }

      pauseYoutubePlayers();

      if (shouldPlay) {
        setIsPlaying(true);
        pendingYoutubeResumeRef.current = {
          videoId: track.youtubeId as string,
          timeSec: 0,
          shouldPlay: true,
        };
      } else {
        pendingYoutubeResumeRef.current = null;
        setIsPlaying(false);
      }

      return;
    }

    pendingYoutubeResumeRef.current = null;

    if (!track.audioUrl) {
      if (player) {
        player.pause();
      }

      pauseYoutubePlayers();
      setIsPlaying(false);
      return;
    }

    if (!shouldPlay) {
      pauseYoutubePlayers();

      if (player) {
        if (player.src !== track.audioUrl) {
          player.src = track.audioUrl;
          player.load();
        }

        player.pause();
      }

      setIsPlaying(false);
      return;
    }

    await playTrack(track);
  };

  const handleNextTrack = async (forcePlay = false) => {
    if (!tracksData.length) {
      return;
    }

    const currentIndex = tracksData.findIndex(
      (track) => track.id === primaryTrack?.id,
    );
    const startIndex = currentIndex >= 0 ? currentIndex : 0;
    const nextIndex = (startIndex + 1) % tracksData.length;
    const nextTrack = tracksData[nextIndex];

    if (!nextTrack) {
      return;
    }

    await activateTrack(nextTrack, forcePlay || isPlaying);
  };
  handleNextTrackRef.current = handleNextTrack;

  const handlePreviousTrack = async () => {
    if (!tracksData.length) {
      return;
    }

    const currentIndex = tracksData.findIndex(
      (track) => track.id === primaryTrack?.id,
    );
    const startIndex = currentIndex >= 0 ? currentIndex : 0;
    const previousIndex =
      (startIndex - 1 + tracksData.length) % tracksData.length;
    const previousTrack = tracksData[previousIndex];

    if (!previousTrack) {
      return;
    }

    await activateTrack(previousTrack, isPlaying);
  };

  const handleSelectTrack = async (track: TrackData) => {
    await activateTrack(track, true);
  };

  const totalDurationSec = useMemo(() => {
    if (albumData?.totalDurationSec) {
      return albumData.totalDurationSec;
    }

    return tracksData.reduce(
      (accumulator, track) => accumulator + (track.durationSec || 0),
      0,
    );
  }, [albumData, tracksData]);

  const totalDurationMinutes = Math.round(totalDurationSec / 60);
  const primaryTrack = activeTrack || tracksData[0];
  const activeYoutubeId =
    primaryTrack?.youtubeId && !primaryTrack?.audioPath
      ? primaryTrack.youtubeId
      : "";

  useEffect(() => {
    const previousIsDesktopViewport = previousIsDesktopViewportRef.current;

    if (previousIsDesktopViewport !== isDesktopViewport && activeYoutubeId) {
      pendingYoutubeResumeRef.current = {
        videoId: activeYoutubeId,
        timeSec: currentTimeSec,
        shouldPlay: isPlaying,
      };
    }

    previousIsDesktopViewportRef.current = isDesktopViewport;
  }, [activeYoutubeId, currentTimeSec, isDesktopViewport, isPlaying]);

  useEffect(() => {
    const pendingYoutubeResume = pendingYoutubeResumeRef.current;

    if (!activeYoutubeId || !pendingYoutubeResume) {
      return;
    }

    if (pendingYoutubeResume.videoId !== activeYoutubeId) {
      pendingYoutubeResumeRef.current = null;
      return;
    }

    const activeYoutubePlayerRef = isDesktopViewport
      ? desktopYoutubeRef
      : mobileYoutubeRef;

    const resumePlayback = () => {
      const youtubePlayer = activeYoutubePlayerRef.current;

      if (!youtubePlayer) {
        return false;
      }

      if (pendingYoutubeResume.timeSec > 0) {
        youtubePlayer.seekTo(pendingYoutubeResume.timeSec);
      }

      if (pendingYoutubeResume.shouldPlay) {
        youtubePlayer.play();
      }

      return true;
    };

    const timeoutIds = [250, 700, 1200, 1800, 2600, 3600, 4800, 6200].map((delayMs) =>
      window.setTimeout(() => {
        resumePlayback();
      }, delayMs),
    );

    return () => {
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, [activeYoutubeId, isDesktopViewport]);

  useEffect(() => {
    if (!activeYoutubeId) {
      return;
    }

    const applyMute = () => {
      const youtubePlayer = isDesktopViewport
        ? desktopYoutubeRef.current
        : mobileYoutubeRef.current;

      if (!youtubePlayer) {
        return false;
      }

      youtubePlayer.setMuted(isMuted);
      return true;
    };

    if (applyMute()) {
      return;
    }

    const timeoutIds = [250, 700, 1200].map((delayMs) =>
      window.setTimeout(() => {
        applyMute();
      }, delayMs),
    );

    return () => {
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, [activeYoutubeId, isDesktopViewport, isMuted]);

  const visibleMobileTracks = tracksData;
  const displayedDurationSec =
    Math.round(audioDurationSec) || primaryTrack?.durationSec || 0;
  const progressPercent = displayedDurationSec
    ? Math.min(100, (currentTimeSec / displayedDurationSec) * 100)
    : 0;

  const handleTrackComplete = useCallback(() => {
    if (!primaryTrack?.id) {
      return;
    }

    if (autoAdvancedTrackIdRef.current === primaryTrack.id) {
      return;
    }

    autoAdvancedTrackIdRef.current = primaryTrack.id;
    handleNextTrackRef.current(true);
  }, [primaryTrack?.id]);

  useEffect(() => {
    autoAdvancedTrackIdRef.current = "";
    autoAdvanceArmedRef.current = false;
  }, [primaryTrack?.id]);

  useEffect(() => {
    if (!primaryTrack?.id || !displayedDurationSec) {
      return;
    }

    // Arm auto-next only after we observe regular playback progress for this track.
    if (!autoAdvanceArmedRef.current && currentTimeSec > 0 && progressPercent < 98) {
      autoAdvanceArmedRef.current = true;
    }

    const remainingSec = displayedDurationSec - currentTimeSec;
    const reachedEndOfBar = progressPercent >= 99.9;
    const reachedEndByTime = remainingSec <= 0.15 && progressPercent >= 99;
    const isTrackComplete =
      autoAdvanceArmedRef.current &&
      currentTimeSec > 0 &&
      (reachedEndOfBar || reachedEndByTime);

    if (isTrackComplete) {
      handleTrackComplete();
    }
  }, [
    currentTimeSec,
    displayedDurationSec,
    handleTrackComplete,
    primaryTrack?.id,
    progressPercent,
  ]);

  const resolveSeekValues = (
    event: React.MouseEvent<HTMLDivElement>,
    seekBarRef: React.RefObject<HTMLDivElement>,
  ) => {
    const seekBarElement = seekBarRef.current;

    if (!seekBarElement || !displayedDurationSec) {
      return null;
    }

    const bounds = seekBarElement.getBoundingClientRect();
    const rawOffsetX = event.clientX - bounds.left;
    const clampedOffsetX = Math.min(Math.max(rawOffsetX, 0), bounds.width);
    const percent = bounds.width ? clampedOffsetX / bounds.width : 0;
    const targetTimeSec = percent * displayedDurationSec;

    return {
      percent,
      targetTimeSec,
    };
  };

  const handleSeek = (targetTimeSec: number) => {
    if (!displayedDurationSec) {
      return;
    }

    const safeTargetTimeSec = Math.min(
      Math.max(targetTimeSec, 0),
      displayedDurationSec,
    );

    if (activeYoutubeId) {
      const youtubePlayer = isDesktopViewport
        ? desktopYoutubeRef.current
        : mobileYoutubeRef.current;

      if (youtubePlayer) {
        youtubePlayer.seekTo(safeTargetTimeSec);
      } else {
        pendingYoutubeResumeRef.current = {
          videoId: activeYoutubeId,
          timeSec: safeTargetTimeSec,
          shouldPlay: isPlaying,
        };
      }

      setCurrentTimeSec(safeTargetTimeSec);
      return;
    }

    const player = audioRef.current;

    if (!player) {
      return;
    }

    player.currentTime = safeTargetTimeSec;
    setCurrentTimeSec(safeTargetTimeSec);
  };

  const handleDesktopSeekMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const seekValues = resolveSeekValues(event, desktopSeekBarRef);

    if (!seekValues) {
      return;
    }

    setDesktopSeekHoverPercent(seekValues.percent * 100);
    setDesktopSeekHoverTimeSec(seekValues.targetTimeSec);
  };

  const handleDesktopSeekClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const seekValues = resolveSeekValues(event, desktopSeekBarRef);

    if (!seekValues) {
      return;
    }

    handleSeek(seekValues.targetTimeSec);
  };

  const handleMobileSeekClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const seekValues = resolveSeekValues(event, mobileSeekBarRef);

    if (!seekValues) {
      return;
    }

    handleSeek(seekValues.targetTimeSec);
  };

  const handleToggleMute = () => {
    const player = audioRef.current;

    setIsMuted((previousMuted) => {
      const nextMuted = !previousMuted;

      if (player) {
        player.muted = nextMuted;
      }

      desktopYoutubeRef.current?.setMuted(nextMuted);
      mobileYoutubeRef.current?.setMuted(nextMuted);

      return nextMuted;
    });
  };

  const handleMenuAction = (label: string) => {
    if (label === "Início") {
      navigate("/");
    }
    if (label === "Biblioteca") {
      navigate("/playlist");
    }
  };

  if (!albumId) {
    return (
      <AlbumSelectionPage
        albums={availableAlbums}
        isLoading={isAlbumsLoading}
        errorMessage={albumsErrorMessage}
        sidebarItems={sidebarItems}
        bottomTabs={bottomTabs}
        activeMenuLabel={activeMenuLabel}
        onMenuAction={handleMenuAction}
        onSelectAlbum={(selectedAlbumId) =>
          navigate(`/playlist/${selectedAlbumId}`)
        }
      />
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-player text-white flex items-center justify-center">
        <p className="text-player-secondary">Carregando álbum...</p>
      </div>
    );
  }

  if (errorMessage || !albumData) {
    return (
      <div className="min-h-screen bg-player text-white flex items-center justify-center px-6 text-center">
        <div>
          <h1 className="text-3xl font-semibold">Playlist</h1>
          <p className="text-player-muted mt-3">
            {errorMessage || "Não foi possível carregar o álbum."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-player text-white">
      <div className="hidden md:flex min-h-screen pb-20">
        <aside className="w-24 border-r border-player-surface px-3 py-6">
          <nav className="space-y-7">
            {sidebarItems.map((item) => (
              <Button variant="ghost" size="none"
                key={item.label}
                onClick={() => handleMenuAction(item.label)}
                className={`w-full flex flex-col items-center ${item.label === activeMenuLabel
                  ? "text-white"
                  : "text-player-secondary hover:text-white"
                  }`}
              >
                {item.icon}
                <span className="mt-2 text-xs">{item.label}</span>
              </Button>
            ))}
          </nav>
        </aside>

        <main className="flex-1 px-8 pt-8 pb-24 bg-gradient-to-b from-player-surface via-player to-player overflow-hidden">
          <div className="max-w-6xl mx-auto flex gap-10 h-full">
            <AlbumPanel
              variant="desktop"
              albumData={albumData}
              mediaContent={
                isDesktopViewport && activeYoutubeId ? (
                  <YoutubePlayer
                    ref={desktopYoutubeRef}
                    videoId={activeYoutubeId}
                    title={primaryTrack?.title || "YouTube video player"}
                    playerId={`playlist-youtube-player-${primaryTrack?.id || "default"}-desktop`}
                    showControls={false}
                    isActive={isDesktopViewport}
                    onReady={() => {
                      if (!isDesktopViewport) {
                        return;
                      }

                      const pendingYoutubeResume = pendingYoutubeResumeRef.current;

                      if (
                        !pendingYoutubeResume ||
                        pendingYoutubeResume.videoId !== activeYoutubeId
                      ) {
                        return;
                      }

                      const youtubePlayer = desktopYoutubeRef.current;

                      if (!youtubePlayer) {
                        return;
                      }

                      youtubePlayer.setMuted(isMuted);

                      if (pendingYoutubeResume.timeSec > 0) {
                        youtubePlayer.seekTo(pendingYoutubeResume.timeSec);
                      }

                      if (pendingYoutubeResume.shouldPlay) {
                        youtubePlayer.play();
                      }
                    }}
                    onPlaybackStateChange={(nextIsPlaying) => {
                      if (!isDesktopViewport) {
                        return;
                      }

                      setIsPlaying(nextIsPlaying);

                      if (
                        nextIsPlaying &&
                        pendingYoutubeResumeRef.current?.videoId === activeYoutubeId
                      ) {
                        pendingYoutubeResumeRef.current = null;
                      }
                    }}
                    onEnded={() => {
                      if (!isDesktopViewport) {
                        return;
                      }

                      handleTrackComplete();
                    }}
                    onTimeDataChange={(nextCurrentTimeSec, nextDurationSec) => {
                      if (!isDesktopViewport) {
                        return;
                      }

                      setCurrentTimeSec(nextCurrentTimeSec);

                      if (nextDurationSec > 0) {
                        setAudioDurationSec(nextDurationSec);
                      }
                    }}
                  />
                ) : null
              }
              tracksCount={tracksData.length}
              totalDurationMinutes={totalDurationMinutes}
              isPlaying={isPlaying}
              primaryTrackTitle={primaryTrack?.title}
              onPlayPause={handlePlayStop}
              onPreviousTrack={handlePreviousTrack}
              onNextTrack={handleNextTrack}
              onBackToCatalog={() => navigate("/playlist")}
              onAction={emptyAction}
            />

            <TrackListPanel
              variant="desktop"
              tracksData={tracksData}
              visibleMobileTracks={visibleMobileTracks}
              primaryTrackId={primaryTrack?.id}
              isPlaying={isPlaying}
              albumData={albumData}
              onSelectTrack={handleSelectTrack}
              formatDuration={formatDuration}
              formatPlayCount={formatPlayCount}
            />
          </div>
        </main>

        <PlayerFooter
          variant="desktop"
          albumData={albumData}
          primaryTrack={primaryTrack}
          isPlaying={isPlaying}
          currentTimeSec={currentTimeSec}
          displayedDurationSec={displayedDurationSec}
          progressPercent={progressPercent}
          bottomTabs={bottomTabs}
          isDesktopSeekHoverVisible={isDesktopSeekHoverVisible}
          desktopSeekHoverPercent={desktopSeekHoverPercent}
          desktopSeekHoverTimeSec={desktopSeekHoverTimeSec}
          desktopSeekBarRef={desktopSeekBarRef}
          mobileSeekBarRef={mobileSeekBarRef}
          onPlayPause={handlePlayStop}
          onPreviousTrack={handlePreviousTrack}
          onNextTrack={handleNextTrack}
          isMuted={isMuted}
          onToggleMute={handleToggleMute}
          onAction={emptyAction}
          onDesktopSeekMouseEnter={() => setIsDesktopSeekHoverVisible(true)}
          onDesktopSeekMouseLeave={() => setIsDesktopSeekHoverVisible(false)}
          onDesktopSeekMouseMove={handleDesktopSeekMove}
          onDesktopSeekClick={handleDesktopSeekClick}
          onMobileSeekClick={handleMobileSeekClick}
          onBottomTabClick={handleMenuAction}
          activeBottomTabLabel={activeMenuLabel}
          formatDuration={formatDuration}
          audioElement={null}
        />
      </div>

      <div className="md:hidden min-h-screen bg-gradient-to-b from-player-surface via-player to-player pb-40 px-4">
        <AlbumPanel
          variant="mobile"
          albumData={albumData}
          mediaContent={
            !isDesktopViewport && activeYoutubeId ? (
              <YoutubePlayer
                ref={mobileYoutubeRef}
                videoId={activeYoutubeId}
                title={primaryTrack?.title || "YouTube video player"}
                playerId={`playlist-youtube-player-${primaryTrack?.id || "default"}-mobile`}
                showControls={false}
                isActive={!isDesktopViewport}
                onReady={() => {
                  if (isDesktopViewport) {
                    return;
                  }

                  const pendingYoutubeResume = pendingYoutubeResumeRef.current;

                  if (
                    !pendingYoutubeResume ||
                    pendingYoutubeResume.videoId !== activeYoutubeId
                  ) {
                    return;
                  }

                  const youtubePlayer = mobileYoutubeRef.current;

                  if (!youtubePlayer) {
                    return;
                  }

                  youtubePlayer.setMuted(isMuted);

                  if (pendingYoutubeResume.timeSec > 0) {
                    youtubePlayer.seekTo(pendingYoutubeResume.timeSec);
                  }

                  if (pendingYoutubeResume.shouldPlay) {
                    youtubePlayer.play();
                  }
                }}
                onPlaybackStateChange={(nextIsPlaying) => {
                  if (isDesktopViewport) {
                    return;
                  }

                  setIsPlaying(nextIsPlaying);

                  if (
                    nextIsPlaying &&
                    pendingYoutubeResumeRef.current?.videoId === activeYoutubeId
                  ) {
                    pendingYoutubeResumeRef.current = null;
                  }
                }}
                onEnded={() => {
                  if (isDesktopViewport) {
                    return;
                  }

                  handleTrackComplete();
                }}
                onTimeDataChange={(nextCurrentTimeSec, nextDurationSec) => {
                  if (isDesktopViewport) {
                    return;
                  }

                  setCurrentTimeSec(nextCurrentTimeSec);

                  if (nextDurationSec > 0) {
                    setAudioDurationSec(nextDurationSec);
                  }
                }}
              />
            ) : null
          }
          tracksCount={tracksData.length}
          totalDurationMinutes={totalDurationMinutes}
          isPlaying={isPlaying}
          primaryTrackTitle={primaryTrack?.title}
          onPlayPause={handlePlayStop}
          onPreviousTrack={handlePreviousTrack}
          onNextTrack={handleNextTrack}
          onBackToCatalog={() => navigate("/playlist")}
          onAction={emptyAction}
        />

        <TrackListPanel
          variant="mobile"
          tracksData={tracksData}
          visibleMobileTracks={visibleMobileTracks}
          primaryTrackId={primaryTrack?.id}
          isPlaying={isPlaying}
          albumData={albumData}
          onSelectTrack={handleSelectTrack}
          formatDuration={formatDuration}
          formatPlayCount={formatPlayCount}
        />

        <PlayerFooter
          variant="mobile"
          albumData={albumData}
          primaryTrack={primaryTrack}
          isPlaying={isPlaying}
          currentTimeSec={currentTimeSec}
          displayedDurationSec={displayedDurationSec}
          progressPercent={progressPercent}
          bottomTabs={bottomTabs}
          isDesktopSeekHoverVisible={isDesktopSeekHoverVisible}
          desktopSeekHoverPercent={desktopSeekHoverPercent}
          desktopSeekHoverTimeSec={desktopSeekHoverTimeSec}
          desktopSeekBarRef={desktopSeekBarRef}
          mobileSeekBarRef={mobileSeekBarRef}
          onPlayPause={handlePlayStop}
          onPreviousTrack={handlePreviousTrack}
          onNextTrack={handleNextTrack}
          isMuted={isMuted}
          onToggleMute={handleToggleMute}
          onAction={emptyAction}
          onDesktopSeekMouseEnter={() => setIsDesktopSeekHoverVisible(true)}
          onDesktopSeekMouseLeave={() => setIsDesktopSeekHoverVisible(false)}
          onDesktopSeekMouseMove={handleDesktopSeekMove}
          onDesktopSeekClick={handleDesktopSeekClick}
          onMobileSeekClick={handleMobileSeekClick}
          onBottomTabClick={handleMenuAction}
          activeBottomTabLabel={activeMenuLabel}
          formatDuration={formatDuration}
          audioElement={
            <audio
              ref={audioRef}
              preload="none"
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onTimeUpdate={() => {
                if (!audioRef.current) {
                  return;
                }

                setCurrentTimeSec(audioRef.current.currentTime || 0);
              }}
              onLoadedMetadata={() => {
                if (!audioRef.current) {
                  return;
                }

                setAudioDurationSec(audioRef.current.duration || 0);
              }}
              onEnded={() => {
                handleTrackComplete();
              }}
            />
          }
        />
      </div>
    </div>
  );
}

export default Playlist;
